// Homepage redesign thresholds (Asana 1218957945501621). Every number the
// ticket names lives here so tuning is a deploy, not a migration. Values that
// mirror a backend constant say so.

import { OnboardingTaskKey } from 'types/interfaces/onboarding';
import { AssetUrls } from 'config/assets';

/** The community map and the home stats refetch on this cadence; the backend caches the community map for 60s. */
export const MAP_REFRESH_MS = 60000;

/** Routes per layer the frontend will draw. The backend already caps at this; the client clamps defensively. */
export const MAP_MAX_ROUTES = 500;

/** Moving dots, placed on the highest-count in-transit routes, and only below MAP_DOTS_MAX_TRANSIT. */
export const MAP_MAX_ANIMATED = 60;

/** At or above this many packages in transit (stats.inTransitCount) no dots render; the dash motion carries "live". */
export const MAP_DOTS_MAX_TRANSIT = 100;

/** Total community arcs (both layers, after the cap) at or below which the SVG painter is used; above, canvas. */
export const MAP_SVG_ARC_LIMIT = 600;

/**
 * The mobile animation budget (Asana 1219037524704691). On useIsMobile()
 * viewports the community layers render static on canvas, no dots render, and
 * at most this many of the viewer's own in-transit arcs keep their march.
 */
export const MAP_MOBILE_MAX_ANIMATED = 8;

/** Canvas backing store: min(devicePixelRatio, this) on every device; 3x buys nothing at 240px and costs 2.25x the pixels. */
export const MAP_MAX_DPR = 2;

/** Per-frame map cost the mobile budget must stay under on the profiled device, else mobile falls back to fully static. */
export const MAP_MOBILE_FRAME_BUDGET_MS = 8;

/** Reported in the PR when one region holds more than this share of routes; nothing acts on it (region toggle is a follow-up). */
export const MAP_REGION_DOMINANCE = 0.8;

/** Desktop and phone map heights, per the mockups (turn 6 and 7). */
export const MAP_HEIGHT_DESKTOP = 440;
export const MAP_HEIGHT_MOBILE = 240;

/** Heat strip: cells per row, oldest on the left. */
export const HEAT_WEEKS = 12;

/** Alpha per intensity step, step 1 to 5. */
export const HEAT_STEPS = [0.15, 0.35, 0.55, 0.75, 1.0] as const;

/** Intensity is scaled against this percentile of the row's own trailing weeks. */
export const HEAT_PCTL = 0.9;

/** Pink hue when pink-band points reach this share of the week's score. */
export const PINK_MAJORITY = 0.5;

export type HeatBand = 'purple' | 'pink';

export type HeatEventKey =
  | 'login'
  | 'inboxMessage'
  | 'haveAdd'
  | 'wantAdd'
  | 'sendPoints'
  | 'tradeSuggest'
  | 'tradeComplete'
  | 'offerSent'
  | 'offerResponded';

export interface HeatWeight {
  band: HeatBand;
  weight: number;
  /** Weekly per-user cap. The BACKEND applies it before the counts reach us; recorded here as documentation only. */
  cap: number | null;
  label: string;
}

/** Design turn 3a, 2026-09. Purple = presence, pink = trade intent. */
export const HEAT_WEIGHTS: Record<HeatEventKey, HeatWeight> = {
  login: { band: 'purple', weight: 1, cap: 7, label: 'logins' },
  inboxMessage: { band: 'purple', weight: 1, cap: 5, label: 'messages' },
  haveAdd: { band: 'purple', weight: 2, cap: 10, label: 'have adds' },
  wantAdd: { band: 'purple', weight: 3, cap: 10, label: 'want adds' },
  sendPoints: { band: 'purple', weight: 5, cap: null, label: 'points sent' },
  tradeSuggest: { band: 'pink', weight: 8, cap: null, label: 'suggestions' },
  tradeComplete: { band: 'pink', weight: 13, cap: null, label: 'trades completed' },
  offerSent: { band: 'pink', weight: 21, cap: null, label: 'offers sent' },
  offerResponded: { band: 'pink', weight: 34, cap: null, label: 'offers answered' },
};

/** Order the events are listed in a tooltip. */
export const HEAT_EVENT_ORDER: HeatEventKey[] = [
  'login',
  'inboxMessage',
  'haveAdd',
  'wantAdd',
  'sendPoints',
  'tradeSuggest',
  'tradeComplete',
  'offerSent',
  'offerResponded',
];

/**
 * Priority rule 4: the quickest open task wins. Ticket order [email, x, bsky,
 * w1, addr, w10, send, w50] in real task keys.
 */
export const SETUP_EFFORT_ORDER: OnboardingTaskKey[] = [
  'VERIFY_EMAIL',
  'FOLLOW_TWITTER',
  'FOLLOW_BLUESKY',
  'ADD_WANT_1',
  'ADD_ADDRESS',
  'ADD_WANT_10',
  'SEND_FIRST_TRADE',
  'ADD_WANT_50',
];

/**
 * Backend TradeService.initiateTrade throws without shippingAddress1 on both
 * sides (VERIFY-FIRST 5, 2026-09-28), so the address gates the two trade tasks
 * and state E exists. Flip to false only if that check is removed.
 */
export const SEND_REQUIRES_ADDRESS = true;

export const SETUP_TOTAL_TASKS = 10;

/** Recent Activity cards on the homepage row. */
export const RECENT_ACTIVITY_COUNT = 8;

/** The "Setup card hidden" toast keeps its Undo for this long. */
export const TOAST_UNDO_MS = 6000;

/** Trader mode on the map when the user's last trade is within this many days. */
export const TRADER_MODE_WINDOW_DAYS = 30;

/** The pink live dot in the welcome header: one opacity pulse per this many ms. */
export const WELCOME_PULSE_MS = 1800;

/**
 * Eric's reviews (2026-09-28): the map runs slower than the first cut, first
 * 0.67 (33% slower), then 0.33 (another 50%). Every moving-dot traversal and
 * every dash march duration is divided by this.
 */
export const MAP_ANIMATION_SPEED = 0.33;

/** Ranked rows on the homepage Leaderboard card (review: match the News card's height). */
export const LEADERBOARD_ROWS = 10;

/**
 * The static banner under the Leaderboard rows (Asana 1219036895682586):
 * Planewide Celebration, War of the Spark #172, art by Wisnu Tan. Same card-image
 * pipeline as the card page's Top Decks art crops; swap the id to change the art.
 */
export const LEADERBOARD_BANNER_SCRYFALL_ID = '11016840-56b6-4759-bde6-3dc0e339e87e';
export const LEADERBOARD_BANNER_ART = AssetUrls.cards.getCardImage(LEADERBOARD_BANNER_SCRYFALL_ID, 'art_crop');
export const LEADERBOARD_BANNER_COPY = "A Planewide Celebration for the community's top traders.";

/**
 * The Pulse trades tile's rolling window in hours (design pass 4, 2026-09-29).
 * MIRRORS the backend's PULSE_TRADES_WINDOW_H in ActivityService; the wire field
 * is `tradesLast24h`, so changing one means changing the other and the field name.
 */
export const PULSE_TRADES_WINDOW_H = 24;
